import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Save } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getErrorMessage } from "@/lib/errors";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function OrgSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/admin/org-settings").then(({ data }) => setForm(data)).catch((err) => toast.error(getErrorMessage(err))); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/org-settings", {
        fiscal_year_start_month: form.fiscal_year_start_month,
        indent_to_po_tolerance_pct: form.indent_to_po_tolerance_pct,
        po_to_grn_tolerance_pct: form.po_to_grn_tolerance_pct,
        bill_rate_tolerance_pct: form.bill_rate_tolerance_pct,
        allow_negative_stock: form.allow_negative_stock,
        decimal_places: form.decimal_places,
      });
      setForm(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    try {
      const { data } = await api.post("/admin/org-settings/smtp-test", { to_email: testEmail });
      toast.success(data.detail);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  if (!form) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="org-settings-page">
      <PageHeader title="Organization Settings" description="Fiscal year, matching tolerances, stock policy and SMTP." />
      <div className="max-w-xl space-y-5 rounded-xl border border-border bg-card p-6">
        <div>
          <Label>Fiscal year starts in</Label>
          <Select value={String(form.fiscal_year_start_month)} onValueChange={(v) => setForm({ ...form, fiscal_year_start_month: Number(v) })}>
            <SelectTrigger className="mt-1.5" data-testid="fiscal-year-start-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Indent → PO tolerance %</Label><Input type="number" step="0.5" value={form.indent_to_po_tolerance_pct} onChange={(e) => setForm({ ...form, indent_to_po_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="indent-to-po-tolerance-input" /></div>
          <div><Label>PO → GRN tolerance %</Label><Input type="number" step="0.5" value={form.po_to_grn_tolerance_pct} onChange={(e) => setForm({ ...form, po_to_grn_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="po-to-grn-tolerance-input" /></div>
          <div><Label>Bill rate tolerance %</Label><Input type="number" step="0.5" value={form.bill_rate_tolerance_pct} onChange={(e) => setForm({ ...form, bill_rate_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="bill-rate-tolerance-input" /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">Allow negative stock</p>
            <p className="text-xs text-muted-foreground">Lets goods issues push a warehouse balance below zero.</p>
          </div>
          <Switch checked={form.allow_negative_stock} onCheckedChange={(v) => setForm({ ...form, allow_negative_stock: v })} data-testid="allow-negative-stock-switch" />
        </div>
        <div className="w-40"><Label>Decimal places</Label><Input type="number" min="0" max="4" value={form.decimal_places} onChange={(e) => setForm({ ...form, decimal_places: e.target.value })} className="mt-1.5" data-testid="decimal-places-input" /></div>
        <Button onClick={save} disabled={saving} data-testid="save-org-settings-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings</Button>

        <div className="border-t border-border pt-4">
          <Label>SMTP test</Label>
          <div className="mt-1.5 flex gap-2">
            <Input type="email" placeholder="you@company.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="smtp-test-email-input" />
            <Button variant="outline" onClick={sendTest} disabled={testing || !testEmail} data-testid="smtp-test-send-button">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send test</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
